import { IUser } from '../../shared/models/User.model';
import { UIConstants, isMercuryTenant } from '../../shared/models/Constants';
import { MyAccessRequestResponse } from '../../shared/models/UserAccess.model';
import { getRowActionVisibility, getScopeFromAttributes } from './MyAccessRequests.helpers';
import { LifecycleGridItem, RoleEligibilityEntry } from './MyAccessRequests.types';

export interface BuildLifecycleItemsParams {
  accessData: MyAccessRequestResponse;
  userDetails: IUser;
  tenantName?: string;
  currentAccessMap: { [roleId: string]: string };
  approvedAccesses: Array<{ roleDefinitionId?: string; endDate?: string; allowRemove?: boolean }>;
  accessExpiryReminderWindowDays: number;
  roleEligibilityMap: { [roleId: string]: RoleEligibilityEntry };
}

const resolveRequestType = (accessItem: any): string => {
  if (!isMercuryTenant(accessItem.tenantName)) {
    return accessItem.requestType;
  }

  const isRemove = accessItem.businessJustification?.trim() === 'SelfRemoval'
    || accessItem.businessJustification?.trim() === 'AdminRemoval'
    || accessItem.mode === 'Admin';

  if (isRemove) {
    return UIConstants.RequestType.Remove;
  }

  return accessItem.properties?.editRequest ? 'Update' : 'Add';
};

const resolveRoleName = (accessItem: any): string => {
  const role = accessItem.role;
  if (!isMercuryTenant(accessItem.tenantName) || role.group === role.roleName) {
    return role.roleName;
  }

  return `${role?.group}-${role?.roleName}`;
};

export const buildLifecycleItems = ({
  accessData,
  userDetails,
  tenantName,
  currentAccessMap,
  approvedAccesses,
  accessExpiryReminderWindowDays,
  roleEligibilityMap,
}: BuildLifecycleItemsParams): LifecycleGridItem[] => {
  const sourceData = accessData?.Data || [];
  const allItems: LifecycleGridItem[] = [];

  sourceData.forEach((accessItem) => {
    const role = accessItem.role;
    if (!role) {
      return;
    }

    if (isMercuryTenant(tenantName) && role.group == null) {
      return;
    }

    const roleName = resolveRoleName(accessItem);
    const requestType = resolveRequestType(accessItem);
    const eligibility = roleEligibilityMap[role.roleId] ?? { allowEdit: false, roleAllowRenew: false };

    allItems.push({
      requestId: accessItem.id,
      roleDesc: role.roleDescription,
      role: roleName,
      roleId: role.roleId,
      requestedBy: (accessItem.requestedBy ?? userDetails?.Alias ?? '').split('@')[0],
      mode: accessItem.mode,
      businessJustification: accessItem.businessJustification,
      requestType,
      attributes: role.attributes && role.attributes.length === 0 ? 'NA' : role.attributes,
      scope: getScopeFromAttributes(role.attributes) || 'Not Applicable',
      endDate: accessItem.endDate ? new Date(accessItem.endDate).toLocaleDateString() : 'Not Applicable',
      originalEndDate: accessItem.endDate ?? null,
      requestDate: new Date(accessItem.requestedDate).toLocaleString(),
      completedDate: accessItem.completedDate ? new Date(accessItem.completedDate).toLocaleString() : 'Not Applicable',
      originalRequestedDate: accessItem.requestedDate ?? null,
      originalCompletedDate: accessItem.completedDate ?? null,
      status: accessItem.status,
      userId: userDetails?.PrincipalId,
      user: userDetails?.Alias,
      properties: accessItem.properties,
      requestedFor: accessItem.properties?.onBehalfExternalUser ?? null,
      roleAssignmentId: currentAccessMap[role.roleId] ?? null,
      businessGroup: role.group || role.businessGroup || null,
      permissionLevel: role.roleName,
      allowEdit: eligibility.allowEdit,
      roleAllowRenew: eligibility.roleAllowRenew,
    });
  });

  return allItems.map((item) => {
    const visibility = getRowActionVisibility(
      item,
      allItems,
      tenantName,
      accessExpiryReminderWindowDays,
      approvedAccesses,
    );

    return {
      ...item,
      canEdit: visibility.showEdit,
      canRenew: visibility.showRenew,
      canCancel: visibility.showCancel,
      canRemove: visibility.showRemove,
    };
  });
};
